/**
 * 定制化音频管理器
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { upload, type UploadFileProps } from "@/services/upload";
import { cosUploadVoiceFile } from "@/services/cos-upload-voice-file";
import { generateElevenLabsAudio, generateMinimaxAudio, GenerateAudioProps } from "@/services/audio-generate";
import { saveAudioInfo } from "@/services/audio-save";
import {
  cloneAudioFromVideo,
  CloneAudioFromVideoProps,
} from "@/services/video-clone-audio";
import { useToast } from "@/hooks/use-toast";
import { canUseWindow } from "@/utils/canuseWindow";
import { checkAudioNameDuplicate } from "@/services/users-check-audio-name-duplicate";
import analytics from "@/utils/analytics";
import { iamAccountManager } from "./iam-account-manager";
import { getAudioMetadata } from "@/utils/audio-validator";
import { useMatch, useParams } from "react-router-dom";
import { userManagementManager } from "./agent-dashboard-user-management-manager";
import { SPEND_CREDITS_EVENT, subscriptionHook, subscriptionManager, } from "./subscription-manager";
import { assetsVoiceManager } from "./assets-voice-manager";

type NameChangeEventData = {
  name: string;
};

// 自定义事件类型
type NameChangeEvent = CustomEvent<NameChangeEventData>;

type FileChangeEventData = {
  file: File | null;
};

// 自定义事件类型
type FileChangeEvent = CustomEvent<FileChangeEventData>;

class CustomAudioManager {
  private static instance: CustomAudioManager;
  // 自定义事件名称
  private readonly STORAGE_KEY = "MIUTA_CUSTOM_AUDIO";
  // 上传类型
  private uploadType: "audio" | "video" = "audio";
  // 上传文件
  private file: File | null = null;
  // 上传后的音频/视频文件url
  private url: string = "";
  // 音频名称
  private name: string = "";
  // 生成的任务id
  private taskId: string = "";
  // 生成的音频
  private audio: Audio | null = null;

  private constructor() { }

  // 单例模式
  public static getInstance(): CustomAudioManager {
    if (!CustomAudioManager.instance) {
      CustomAudioManager.instance = new CustomAudioManager();
    }
    return CustomAudioManager.instance;
  }

  /** 初始化 */
  public init({
    uploadType,
    name,
    file,
    url,
  }: {
    uploadType: "audio" | "video";
    name: string;
    file?: File;
    url?: string;
  }) {
    this.setUploadType(uploadType);
    this.setName(name);
    // 调用顺序：先设置文件，再设置URL
    file && this.setFile(file);
    url && this.setUrl(url);
  }

  /** 设置上传类型 */
  private setUploadType(type: "audio" | "video") {
    this.uploadType = type;
  }

  /** 获取上传类型 */
  private getUploadType(): "audio" | "video" {
    return this.uploadType;
  }

  /** 设置上传文件 */
  public setFile(file: File) {
    this.file = file;
    // 重新设置文件需要把URL清除掉
    file && this.setUrl('');
    this.dispatchFileChange(file);
  }

  /** 获取上传文件 */
  public getFile(): File | null {
    return this.file;
  }

  /** 设置上传后的音频/视频文件url */
  private setUrl(url: string) {
    this.url = url;
  }

  /** 获取上传后的音频/视频文件url */
  private getUrl(): string {
    return this.url;
  }

  private getTaskId(): string {
    return this.taskId;
  }

  private setTaskId(taskId: string) {
    this.taskId = taskId;
  }

  public setName(name: string) {
    this.name = name;
    this.dispatchNameChange(name);
  }

  public getName(): string {
    return this.name;
  }

  public setAudio(audio: Audio) {
    this.audio = audio;
  }

  public getAudio(): Audio | null {
    return this.audio;
  }

  // 上传文件 - 音频（废弃）
  private async uploadFile({ file }: UploadFileProps) {
    const res = await upload({ file });
    this.setUrl(res.fileUri); // 文件地址
    return res;
  }

  /** 上传文件 - 音频（COS 直传，voice 场景，当前业务入口） */
  private async cosUploadFile({ file, onProgress }: UploadFileProps) {
    const res = await cosUploadVoiceFile(file, { onProgress });
    this.setUrl(res.url);
    return res;
  }

  /** 生成 ElevenLabs 音频 */
  private async generateAudio({ audioName, audioUrl }: GenerateAudioProps) {
    const res = await generateElevenLabsAudio({ audioName, audioUrl });
    this.setTaskId(res.taskId); // 生成后的任务ID
    return res;
  }

  /** 生成 Minimax 音频 */
  private async generateMinimaxAudio({
    userId,
    audioName,
    audioUrl,
    point,
  }: GenerateAudioProps & { userId?: string }) {
    const res = await generateMinimaxAudio({ userId, audioName, audioUrl, point });
    this.setTaskId(res.taskId); // 生成后的任务ID
    return res;
  }

  /** 克隆音频 */
  private async cloneAudio({ audioName, videoUrl, userId }: CloneAudioFromVideoProps) {
    const res = await cloneAudioFromVideo({ audioName, videoUrl, userId });
    this.setTaskId(res.taskId); // 生成后的任务ID
    return res;
  }

  /** 检查音频名称是否重复 */
  public async checkAudioNameDuplicate(userId?: string) {
    const res = await checkAudioNameDuplicate({ name: this.getName(), userId });
    return res;
  }

  /** 
   * 生成音频
   * * 暂时都使用 Minimax 音频生成
   */
  public async clone(generateType: "ElevenLabs" | "Minimax", userId?: string, point?: number) {
    // 如果类型是视频，克隆音频
    if (this.getUploadType() === "video") {
      return await this.cloneAudio({
        audioName: this.getName(),
        videoUrl: this.getUrl(),
        userId,
      });
    } else if (this.getUploadType() === "audio") {
      if (!this.getUrl()) {
        await this.cosUploadFile({ file: this.getFile()! });
      }
      if (this.getUrl()) {
        // 暂时都使用 Minimax 音频生成
        return await this.generateMinimaxAudio({
          userId,
          audioName: this.getName(),
          audioUrl: this.getUrl(),
          point,
        });
        // // 根据生成类型选择不同的生成方法
        // if (generateType === "ElevenLabs") {
        //   // 生成 ElevenLabs 音频
        //   return await this.generateAudio({
        //     audioName: this.getName(),
        //     audioUrl: this.getUrl(),
        //   });
        // } else if (generateType === "Minimax") {
        //   // 生成 Minimax 音频
        //   return await this.generateMinimaxAudio({
        //     userId,
        //     audioName: this.getName(),
        //     audioUrl: this.getUrl(),
        //   });
        // }
      }
    }
  }

  /** 保存音频 */
  public async save() {
    return await saveAudioInfo(this.getTaskId());
  }

  public reset() {
    this.setUploadType("audio");
    this.setName("");
    this.setFile(null);
    this.setUrl("");
    this.setTaskId("");
    this.setAudio(null);
  }

  public onNameChange(listener: (e: NameChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:name`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:name`, listener);
      };
    }
  }

  public onFileChange(listener: (e: FileChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:file`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:file`, listener);
      };
    }
  }

  private dispatchNameChange(name: string) {
    if (canUseWindow()) {
      const event = new CustomEvent<NameChangeEventData>(
        `${this.STORAGE_KEY}:name`,
        { detail: { name } }
      );
      window.dispatchEvent(event);
    }
  }

  private dispatchFileChange(file: File | null) {
    if (canUseWindow()) {
      window.dispatchEvent(
        new CustomEvent(`${this.STORAGE_KEY}:file`, { detail: { file } })
      );
    }
  }
}

export const useCustomAudio = () => {
  const toast = useToast();
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState("");
  const [isAllowStep, setIsAllowStep] = useState(false);
  // 防抖引用
  const setIsAllowCreateDebounceRef = useRef<number | null>(null);
  // 用于防止旧请求回包覆盖新输入的结果
  const checkNameRequestSeqRef = useRef(0);
  const params = useParams();
  const isAgentUserManagementDetail = useMatch("/agent/dashboard/user-management/:id/:userId");

  // 用户ID，在用户管理详情页时需要传递
  const userId = new URLSearchParams(window.location.search).get('userId');

  /** 初始化 */
  const checkNameDuplicate = useCallback(async (userId?: string) => {
    if (!customAudioManager.getName()) return false;
    try {
      const res = await customAudioManager.checkAudioNameDuplicate(userId);
      if (res.isDuplicate) {
        setMessage("Audit name already exists");
        return false
      }
      return true;
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    }
  }, [toast]);

  const init = useCallback(async ({
    uploadType,
    name,
    file,
    url,
  }: {
    uploadType: "audio" | "video";
    name: string;
    file?: File;
    url?: string;
  }) => {

    customAudioManager.init({ uploadType, name, file, url });

    setIsAllowStep(await checkNameDuplicate(userId));
  }, [checkNameDuplicate, userId]);

  // 检查音频名称是否重复
  const checkName = async () => {
    const requestSeq = ++checkNameRequestSeqRef.current;
    const nameSnapshot = customAudioManager.getName();

    setIsAllowCreateDebounceRef.current = window.setTimeout(async () => {
      try {
        const isAllow = await checkNameDuplicate(userId);
        // 只应用“最后一次输入”的结果，避免乱序回包
        if (requestSeq !== checkNameRequestSeqRef.current) return;
        if (nameSnapshot !== customAudioManager.getName()) return;
        setIsAllowStep(isAllow);
      } finally {
        // 仅清理当前定时器引用（避免误清理后续输入的定时器）
        if (requestSeq === checkNameRequestSeqRef.current) {
          setIsAllowCreateDebounceRef.current = null;
        }
      }
    }, 150);
  };

  // 音频名称
  const setAudioName = (name: string) => {
    customAudioManager.setName(name);
    // 清除之前的防抖定时器
    if (setIsAllowCreateDebounceRef.current !== null) {
      clearTimeout(setIsAllowCreateDebounceRef.current);
      setIsAllowCreateDebounceRef.current = null;
    }
    setIsAllowStep(false);
    setMessage("");
    checkName();
  };

  const setAudioFile = (file: File | null) => {
    customAudioManager.setFile(file);
  };

  useEffect(() => {
    return customAudioManager.onNameChange((e) => {
      setName(e.detail.name);
    });
  }, []);

  useEffect(() => {
    return customAudioManager.onFileChange((e) => {
      setFile(e.detail.file);
    });
  }, []);

  return {
    name,
    file,
    message,
    isAllowStep,
    isAgentUserManagementDetail,
    setAudioName,
    setAudioFile,
    init,
  };
};

export const useCustomAudioClone = () => {
  const toast = useToast();
  const [progress, setProgress] = useState(0);
  const params = useParams();
  const isAgentUserManagementDetail = useMatch("/agent/dashboard/user-management/:id/:userId");

  // 用户ID，在用户管理详情页时需要传递
  const userId = new URLSearchParams(window.location.search).get('userId');

  // 克隆音频
  const clone = async (generateType: "ElevenLabs" | "Minimax", point?: number) => {
    setProgress(0);
    // 模拟进度条
    handleGenerateProgress(0);
    // 获取音频元数据
    const metadata = await getAudioMetadata(customAudioManager.getFile());
    const trackParams = {
      duration: metadata?.duration,
      fileSize: metadata?.fileSize,
      userId: userId || iamAccountManager.get().id,
    }
    // 克隆前埋点
    analytics.trackCloneAudio(trackParams)
    try {
      await customAudioManager.clone(
        generateType,
        // 在智能体用户详情中创建数字人时，需要传递 userId
        isAgentUserManagementDetail ? (userId || params.userId) : undefined,
        point,
      );
      setProgress(100);

      // 刷新列表
      // assetsVoiceManager.fetch();

      // const oldCredits = subscriptionManager.getCredits();
      // 更新积分
      subscriptionManager.fetchAgentCredits();

      // 克隆成功埋点
      analytics.trackCloneAudioSuccess(trackParams)

      // 积分消耗埋点
      // analytics.trackCreditConsumed({
      //   user_email: iamAccountManager.get()?.email,
      //   credits_used: oldCredits - remainCredits,
      //   remaining_balance: remainCredits,
      //   feature_name: SPEND_CREDITS_EVENT.clone_elevenlabs,
      // })
    } catch (error) {
      setProgress(0);
      toast.showToastByCode(error);
      // 克隆失败埋点
      analytics.trackCloneAudioFail(trackParams)
      throw error;
    }
  };

  const handleGenerateProgress = async (progress: number) => {
    return new Promise((resolve) => {
      setProgress(realProgress => {
        if (realProgress > 95) {
          resolve(null);
          return realProgress;
        }
        setTimeout(() => {
          resolve(handleGenerateProgress(progress + 0.8));
        }, 50);
        return progress;
      });
    });
  };

  return {
    progress,
    clone,
  };
};

export const useCustomAudioReset = () => {
  const reset = () => {
    customAudioManager.reset();
  };

  return {
    reset,
  };
};

export const useCustomAudioSave = () => {
  const { showToastByCode } = useToast();
  const params = useParams();
  const isAgentUserManagementDetail = useMatch("/agent/dashboard/user-management/:id/:userId");

  // 用户ID，在用户管理详情页时需要传递
  const userId = new URLSearchParams(window.location.search).get('userId');

  const save = async () => {
    // 保存前埋点
    analytics.trackSaveAudio({ userId: userId || iamAccountManager.get()?.id })
    try {
      const data = await customAudioManager.save();
      customAudioManager.setAudio({
        id: data.id,
        voiceTtsId: data.voiceTtsId,
        audioUrl: data.sampleUrl,
        // 必须有的
        modelName: "",
        name: "",
        ageGroup: "",
        language: "",
        gender: "",
        speed: 1,
        isStarred: false,
      });

      // 保存成功后埋点
      analytics.trackSaveAudioSuccess({ userId: iamAccountManager.get()?.id })

      // 在智能体用户详情中创建音频时，需要刷新用户详情中的音频列表
      if (isAgentUserManagementDetail && userId) {
        await userManagementManager.fetchAgentUserActors();
      }

      return data;
    } catch (error) {
      showToastByCode(error);
      // 保存失败后埋点
      analytics.trackSaveAudioFail({ userId: iamAccountManager.get().id })
      assetsVoiceManager.fetch(); // 刷新音频列表
      throw error;
    }
  };

  return {
    save,
  };
}

export const customAudioHooks = {
  useCustomAudio,
  useCustomAudioClone,
  useCustomAudioReset,
  useCustomAudioSave,
};

export const customAudioManager = CustomAudioManager.getInstance();
